import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import Skeleton from 'react-loading-skeleton'
import { Link } from 'react-router-dom'

import type { GetTerritoryTotalPostsQuery, TaxonomyLanguages } from 'generated/graphql'
import { T, useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import { analyticsTracker, humanizeLargeNumber, TOLGEE_GLOBALS } from 'utils'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import { GET_TERRITORY_TOTAL_POSTS } from './query'
import './index.scss'
import translation from './translation'
import { GET_TERRITORY_TOTAL_POSTS_LOG } from '../../consts/queryLogs'

interface ITerritoryInSearch {
  search: string
  parentName: string
  territoryId: string
  language?: TaxonomyLanguages
  topicIn?: string
}

const { TOP } = analyticsTracker.eventNames

export default function TerritoryInSearch({ parentName, search, territoryId, language, topicIn }: ITerritoryInSearch) {
  const t = useTranslate()

  const variables = useMemo(() => {
    return {
      territoryId,
      timeRange: {
        timeUnit: 'month',
        timeValue: 12,
      },
    }
  }, [territoryId])

  const { data, loading } = useQuery<GetTerritoryTotalPostsQuery>(GET_TERRITORY_TOTAL_POSTS, {
    variables,
    fetchPolicy: 'cache-first',
    context: {
      errorLog: GET_TERRITORY_TOTAL_POSTS_LOG,
    },
  })

  const handleTrack = () => {
    analyticsTracker.eventTrack(TOP.CLICK_RELATED_TOPIC, {
      'Topic name': `${search} in ${parentName}`,
    })
  }

  const nicheLink = useMemo(() => {
    const nicheId = territoryId.split('.')?.[1]
    const currentWorkspaceId = (PanelStore?.selectedPanel as any)?.panelId
    const route = getRoute(PATHNAMES.WORKSPACE_NICHE, {
      panelId: currentWorkspaceId,
      entityId: nicheId,
      tab: 'overview',
    })

    return route
  }, [territoryId])

  return (
    <Link to={nicheLink} onClick={handleTrack} className="TerritoryInSearch-wrapper">
      <div className="titlesSection">
        <p>
          <strong>&quot;{search}&quot;</strong> {t(translation.territoryInSearch)} <strong>{parentName}</strong>
        </p>
        {Boolean(language && topicIn) && (
          <label>
            {t(TOLGEE_GLOBALS[language])} - {topicIn}
          </label>
        )}
      </div>
      <div className="metricSection">
        <p>
          {loading ? (
            <Skeleton height={12} borderRadius={20} width={20} />
          ) : (
            humanizeLargeNumber(data?.getTerritory?.territoryPerformance?.totalPosts)
          )}
        </p>
        <label>
          <T keyName={TOLGEE_GLOBALS.videos.key}>{TOLGEE_GLOBALS.videos.defaultValue}</T>
        </label>
      </div>
    </Link>
  )
}
